
// Dynamic Price Per Serving Calculator with Multi-Currency Support
class PricePerServingUpdater {
  constructor() {
    this.pricePerServingElement = null;
    this.pricingJsElement = null;
    this.savingsElement = null;
    this.subscriptionWidget = null;
    this.servingsPerMonth = 30; // Default from the subscription details
    // Initialize immediately for faster loading
    this.init();
    // Also initialize when DOM is ready as backup
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    }
    
    // Early initialization attempt
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      setTimeout(() => this.init(), 0);
    }
  }

  init() {
    this.findElements();
    this.setupObservers();
    this.setupCurrencyChangeListener();
    this.updatePricePerServing();
  }

  findElements() {
    // Find the price per serving element
    this.pricePerServingElement = document.querySelector('.js-price-per-serving');
    
    // Find the pricing JS element
    this.pricingJsElement = document.querySelector('#pricingjs');
    
    // Find the savings element - try multiple selectors to be sure
    this.savingsElement = document.querySelector('savings-amount[form*="product-form"]') || 
                         document.querySelector('savings-amount.savings') ||
                         document.querySelector('.savings');
    
    // Find the subscription widget (may not be loaded yet)
    this.subscriptionWidget = document.querySelector('#juo-subscription-widget');
  }

  setupCurrencyChangeListener() {
    // Listen for Shopify currency change events
    document.addEventListener('currency:changed', () => {
      console.log('Currency changed, updating prices...');
      this.updatePricePerServing();
    });

    // Also listen for custom events that might be triggered by your currency switcher
    window.addEventListener('currencyChanged', () => {
      this.updatePricePerServing();
    });

    // Listen for changes to the Shopify.currency object
    if (window.Shopify && window.Shopify.currency) {
      const originalCurrency = window.Shopify.currency.active;
      setInterval(() => {
        if (window.Shopify.currency.active !== originalCurrency) {
          this.updatePricePerServing();
        }
      }, 1000);
    }
  }

  getCurrentCurrency() {
    // Try multiple methods to get the current currency
    // Method 1: Shopify.currency object
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    
    // Method 2: Check for currency in DOM (common in many themes)
    const currencyElement = document.querySelector('[data-currency]');
    if (currencyElement) {
      return currencyElement.getAttribute('data-currency');
    }
    
    // Method 3: Extract from existing money elements
    const moneyElement = document.querySelector('.money');
    if (moneyElement) {
      const currencyMatch = moneyElement.textContent.match(/([A-Z]{3})/);
      if (currencyMatch) {
        return currencyMatch[1];
      }
    }
    
    // Method 4: Check meta tags
    const metaCurrency = document.querySelector('meta[property="og:price:currency"]');
    if (metaCurrency) {
      return metaCurrency.content;
    }
    
    // Default fallback
    return 'EUR';
  }

  getCurrencySymbol(currency) {
    // Map of common currency symbols
    const symbols = {
      'EUR': '€',
      'USD': '$',
      'GBP': '£',
      'CAD': 'CA$',
      'AUD': 'AU$',
      'JPY': '¥',
      'CHF': 'CHF',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'PLN': 'zł',
      'CZK': 'Kč'
    };
    
    return symbols[currency] || currency + ' ';
  }

  formatMoneyLikeShopify(amount, currency) {
    // Try to use Shopify's money formatting if available
    if (window.Shopify && window.Shopify.formatMoney) {
      // Convert to cents for Shopify formatting
      const amountInCents = Math.round(amount * 100);
      return window.Shopify.formatMoney(amountInCents, window.Shopify.money_format || '');
    }
    
    // Fallback to manual formatting
    const symbol = this.getCurrencySymbol(currency);
    const formattedAmount = amount.toFixed(2);
    
    // Always put currency symbol before the number
    return `${symbol}${formattedAmount}`;
  }

  setupObservers() {
    
    // Immediate check for existing elements
    this.updatePricePerServing();
    
    
    // Observer for when the subscription widget loads
    const widgetObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.id === 'juo-subscription-widget' || node.querySelector('#juo-subscription-widget')) {
              
              this.subscriptionWidget = document.querySelector('#juo-subscription-widget');
              this.attachRadioListeners();
              this.updatePricePerServing();
            }
          }
        });
      });
    });

    // Observer for changes within the subscription widget
    const priceObserver = new MutationObserver(() => {
      this.updatePricePerServing();
    });

    // Start observing
    console.log(widgetObserver,'widgetObserver');
    widgetObserver.observe(document.body, { 
      childList: true,
      subtree: true
    });

    // If widget already exists, set up listeners immediately
    if (this.subscriptionWidget) {
      this.attachRadioListeners();
      priceObserver.observe(this.subscriptionWidget, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }


    // Aggressive checking for widget with shorter intervals
    const checkForWidget = setInterval(() => {
      if (!this.subscriptionWidget) {
        this.subscriptionWidget = document.querySelector('#juo-subscription-widget');
        if (this.subscriptionWidget) {
          this.attachRadioListeners();
          priceObserver.observe(this.subscriptionWidget, {
            childList: true,
            subtree: true,
            characterData: true
          });
          this.updatePricePerServing();
          clearInterval(checkForWidget);
        }
      }
    }, 50);

    // Clear the interval after 5 seconds
    setTimeout(() => clearInterval(checkForWidget), 5000);
  }

  attachRadioListeners() {
    if (!this.subscriptionWidget) return;

    // Listen specifically to the subscription option
    const subscriptionOption = this.subscriptionWidget.querySelector('.j-subscription');
    const otpOption = this.subscriptionWidget.querySelector('.j-otp');

    if (subscriptionOption) {
      subscriptionOption.addEventListener('click', () => {
        console.log('Subscription clicked');
        setTimeout(() => this.updatePriceFromSubscription(), 50);
      });
    }

    if (otpOption) {
      otpOption.addEventListener('click', () => {
        console.log('OTP clicked');
        setTimeout(() => this.updatePriceFromOTP(), 50);
      });
    }

    // Also listen to radio buttons as backup
    const radioButtons = this.subscriptionWidget.querySelectorAll('input[type="radio"][name="mode"]');
    radioButtons.forEach(radio => {
      radio.addEventListener('change', () => {
        console.log('Radio changed to:', radio.value);
        if (radio.value === 'subscription') {
          this.updatePriceFromSubscription();
        } else if (radio.value === 'otp') {
          this.updatePriceFromOTP();
        }
      });
    });

    // Add click listeners to any parent elements that might contain the radio buttons
    const allClickableElements = this.subscriptionWidget.querySelectorAll('[data-mode], .subscription-option, .otp-option');
    allClickableElements.forEach(element => {
      element.addEventListener('click', () => {
        setTimeout(() => {
          const checkedRadio = this.subscriptionWidget.querySelector('input[type="radio"][name="mode"]:checked');
          if (checkedRadio) {
            console.log('Detected selection via click:', checkedRadio.value);
            if (checkedRadio.value === 'subscription') {
              this.updatePriceFromSubscription();
            } else if (checkedRadio.value === 'otp') {
              this.updatePriceFromOTP();
            }
          }
        }, 100);
      });
    });
  }

  updatePriceFromSubscription() {
    const subscriptionPriceElement = this.subscriptionWidget?.querySelector('[part="subscription-price"] .money');
    if (subscriptionPriceElement) {
      const price = this.extractPrice(subscriptionPriceElement.textContent);
      this.calculateAndUpdatePricePerServing(price);
      this.updatePricingJs(price);
      this.showSavings(); // Show savings for subscription
      console.log('Updated with subscription price:', price);
    }
  }

  updatePriceFromOTP() {
    const otpPriceElement = this.subscriptionWidget?.querySelector('.j-otp .money');
    if (otpPriceElement) {
      const price = this.extractPrice(otpPriceElement.textContent);
      this.calculateAndUpdatePricePerServing(price);
      this.updatePricingJs(price);
      this.hideSavings(); // Hide savings for one-time payment
      console.log('Updated with OTP price:', price);
    }
  }

  extractPrice(priceText) {
    if (!priceText) return null;
    // Extract numeric value, handling different number formats
    const cleanedText = priceText.replace(/[^\d,.-]/g, '');
    // Handle both comma and period as decimal separators
    const normalizedText = cleanedText.replace(',', '.');
    const priceMatch = normalizedText.match(/[\d.]+/);
    return priceMatch ? parseFloat(priceMatch[0]) : null;
  }

  calculateAndUpdatePricePerServing(price) {
    if (!this.pricePerServingElement || price === null) return;

    const pricePerServing = price / this.servingsPerMonth;
    const currentCurrency = this.getCurrentCurrency();
    const formattedPrice = this.formatMoneyLikeShopify(pricePerServing, currentCurrency);
    
    this.pricePerServingElement.textContent = `${formattedPrice} / Serving`;
    console.log('Price per serving updated to:', formattedPrice, 'Currency:', currentCurrency);
  }

  updatePricingJs(price) {
    if (!this.pricingJsElement || price === null) return;

    const currentCurrency = this.getCurrentCurrency();
    const formattedPrice = this.formatMoneyLikeShopify(price, currentCurrency);
    
    this.pricingJsElement.textContent = formattedPrice;
    console.log('Pricing JS updated to:', formattedPrice, 'Currency:', currentCurrency);
  }

  showSavings() {
    if (this.savingsElement) {
      this.savingsElement.removeAttribute('hidden');
      this.savingsElement.style.display = '';
      this.savingsElement.style.visibility = 'visible';
      console.log('Savings element shown');
    }
  }

  hideSavings() {
    if (this.savingsElement) {
      this.savingsElement.setAttribute('hidden', 'true');
      this.savingsElement.style.display = 'none';
      this.savingsElement.style.visibility = 'hidden';
      console.log('Savings element hidden');
    } else {
      // Try to find the element again if it wasn't found initially
      const savingsEl = document.querySelector('savings-amount[form*="product-form"]') || 
                       document.querySelector('savings-amount.savings') ||
                       document.querySelector('.savings');
      if (savingsEl) {
        this.savingsElement = savingsEl;
        savingsEl.setAttribute('hidden', 'true');
        savingsEl.style.display = 'none';
        savingsEl.style.visibility = 'hidden';
        console.log('Savings element found and hidden');
      }
    }
  }

  updatePricePerServing() {
    // Try to determine which option should be active and update accordingly
    if (!this.subscriptionWidget) return;

    const checkedRadio = this.subscriptionWidget.querySelector('input[type="radio"][name="mode"]:checked');
    if (checkedRadio) {
      if (checkedRadio.value === 'subscription') {
        this.updatePriceFromSubscription();
      } else if (checkedRadio.value === 'otp') {
        this.updatePriceFromOTP();
      }
    } else {
      // Default to subscription if no radio is checked
      this.updatePriceFromSubscription();
    }
  }

  // Public method to manually trigger update
  refresh() {
    this.findElements();
    this.updatePricePerServing();
  }
}

// Initialize the price updater immediately
const priceUpdater = new PricePerServingUpdater();
console.log(priceUpdater, 'priceUpdater');
// Additional early initialization attempts for maximum speed
setTimeout(() => {
  if (priceUpdater) {
    priceUpdater.refresh();
  }
}, 10);

setTimeout(() => {
  if (priceUpdater) {
    priceUpdater.refresh();
  }
}, 100);

// Expose globally for manual refresh if needed
window.refreshPricePerServing = () => priceUpdater.refresh();

// Also expose a method to manually trigger currency update
window.updatePricePerServingCurrency = () => priceUpdater.updatePricePerServing();



  const redirectPages = [
    '/pages/quiz',
    '/pages/longevity-academy',
    '/pages/scientific-advisory-board'
  ];

  if (redirectPages.includes(window.location.pathname)) {
    window.location.replace('/');
  }



  document.addEventListener("DOMContentLoaded", function () {
    if (window.location.pathname.endsWith("longevity-complete-eu2")) {
      window.location.replace("/products/longevity-complete");
    }
  });

